const eclipseMotionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
const eclipseMobileViewport = window.matchMedia('(max-width: 749px)');
const eclipseClamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const eclipseEaseOut = (value) => 1 - Math.pow(1 - eclipseClamp(value), 3);

function eclipseWait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function eclipseDocumentReady() {
  if (document.readyState !== 'loading') return Promise.resolve();
  return new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
}

function eclipseDecodeImage(image) {
  if (image.complete) {
    if (typeof image.decode === 'function') return image.decode().catch(() => undefined);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', resolve, { once: true });
  });
}

function eclipsePerformanceMode() {
  const root = document.documentElement;
  const selected = root.dataset.eclipseMobilePerformance || 'auto';
  let resolved = selected;

  if (selected === 'auto') {
    if (!eclipseMobileViewport.matches) {
      resolved = 'full';
    } else {
      const memory = Number(navigator.deviceMemory || 4);
      const cores = Number(navigator.hardwareConcurrency || 4);
      resolved = memory <= 2 || cores <= 4 ? 'minimal' : 'balanced';
    }
  }

  root.dataset.eclipseResolvedMobilePerformance = resolved;
  return resolved;
}

function eclipseFrameInterval() {
  if (!eclipseMobileViewport.matches) return 0;
  const mode = eclipsePerformanceMode();
  if (mode === 'minimal') return 32;
  if (mode === 'balanced' || mode === 'auto') return 18;
  return 0;
}

class EclipsePreloader extends HTMLElement {
  connectedCallback() {
    if (!document.documentElement.classList.contains('eclipse-preloader-active')) {
      this.remove();
      return;
    }

    if (eclipseMotionPreference.matches || window.Shopify?.designMode) {
      this.completeImmediately();
      return;
    }

    this.bar = this.querySelector('[data-eclipse-loader-bar]');
    this.percentage = this.querySelector('[data-eclipse-loader-percentage]');
    this.status = this.querySelector('[data-eclipse-loader-status]');
    this.minimumDuration = Math.max(500, Number(this.dataset.minDuration) || 1300);
    this.interfaceDelay = Math.max(0, Number(this.dataset.interfaceDelay) || 280);
    this.holdDuration = Math.max(0, Number(this.dataset.hold) || 320);
    this.maximumWait = Math.max(this.minimumDuration, Number(this.dataset.maxWait) || 4500);
    this.labels = {
      primary: this.dataset.loadingPrimary || 'ESTABLISHING SIGNAL',
      secondary: this.dataset.loadingSecondary || 'ACCESSING ECLIPSE',
      tertiary: this.dataset.loadingTertiary || 'RENDERING INTERFACE',
      complete: this.dataset.complete || 'ACCESS GRANTED',
    };
    this.progress = 0;
    this.startedAt = performance.now();
    this.ready = false;
    this.finished = false;

    this.interfaceTimer = window.setTimeout(() => this.classList.add('is-interface-ready'), this.interfaceDelay);
    this.setProgress(2);

    Promise.race([this.waitForCriticalContent(), eclipseWait(this.maximumWait)]).then(() => {
      this.ready = true;
    });

    this.frame = window.requestAnimationFrame((time) => this.tick(time));
  }

  disconnectedCallback() {
    if (this.frame) window.cancelAnimationFrame(this.frame);
    if (this.interfaceTimer) window.clearTimeout(this.interfaceTimer);
  }

  async waitForCriticalContent() {
    await eclipseDocumentReady();

    const fontPromise = document.fonts?.ready?.catch?.(() => undefined) || Promise.resolve();
    const imageLimit = eclipseMobileViewport.matches ? 3 : 6;
    const criticalImages = [...document.querySelectorAll('main img[loading="eager"], main img[fetchpriority="high"]')]
      .filter((image) => !this.contains(image))
      .slice(0, imageLimit)
      .map(eclipseDecodeImage);

    await Promise.allSettled([fontPromise, ...criticalImages]);
  }

  tick(now) {
    if (this.finished) return;

    const elapsed = now - this.startedAt;
    const timeProgress = eclipseClamp(elapsed / this.minimumDuration);
    this.setProgress(4 + eclipseEaseOut(timeProgress) * 86);

    if (this.ready && elapsed >= this.minimumDuration) {
      this.finish();
      return;
    }

    this.frame = window.requestAnimationFrame((time) => this.tick(time));
  }

  setProgress(value) {
    this.progress = Math.max(this.progress, eclipseClamp(value, 0, 100));
    if (this.bar) this.bar.style.transform = `scaleX(${this.progress / 100})`;
    if (this.percentage) this.percentage.textContent = String(Math.round(this.progress)).padStart(2, '0');

    if (this.status) {
      if (this.progress < 36) this.status.textContent = this.labels.primary;
      else if (this.progress < 72) this.status.textContent = this.labels.secondary;
      else if (this.progress < 100) this.status.textContent = this.labels.tertiary;
      else this.status.textContent = this.labels.complete;
    }
  }

  finish() {
    if (this.finished) return;
    this.finished = true;

    const from = this.progress;
    const duration = 300;
    const start = performance.now();

    const completeProgress = (now) => {
      const progress = eclipseEaseOut((now - start) / duration);
      this.setProgress(from + (100 - from) * progress);
      if (progress < 1) {
        this.frame = window.requestAnimationFrame(completeProgress);
        return;
      }

      this.classList.add('is-granted');
      try { sessionStorage.setItem('eclipse-entry-granted-v4', '1'); } catch (error) { /* Storage can be unavailable. */ }

      window.setTimeout(() => {
        this.classList.add('is-exiting');
        window.setTimeout(() => this.completeImmediately(), 900);
      }, this.holdDuration);
    };

    this.frame = window.requestAnimationFrame(completeProgress);
  }

  completeImmediately() {
    document.documentElement.classList.remove('eclipse-preloader-active');
    document.documentElement.classList.add('eclipse-preloader-skipped');
    this.classList.add('is-complete');
    this.dispatchEvent(new CustomEvent('eclipse:ready', { bubbles: true }));
    window.setTimeout(() => this.remove(), 50);
  }
}

if (!customElements.get('eclipse-preloader')) {
  customElements.define('eclipse-preloader', EclipsePreloader);
}

function eclipseScrollContainer() {
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper) {
    const styles = getComputedStyle(pageWrapper);
    const scrollable = ['auto', 'scroll'].includes(styles.overflowY) && pageWrapper.scrollHeight > pageWrapper.clientHeight + 2;
    if (scrollable) return pageWrapper;
  }
  return window;
}

function eclipseScrollTop(target) {
  return target === window ? window.scrollY : target.scrollTop;
}

function eclipseViewportHeight(target) {
  return target === window ? window.innerHeight : target.clientHeight;
}

class EclipseScrollScene extends HTMLElement {
  connectedCallback() {
    this.scrollTarget = eclipseScrollContainer();
    this.visible = true;
    this.frame = 0;
    this.lastRenderTime = 0;
    this.lastProgress = -1;
    this.onScroll = () => this.requestRender();
    this.onResize = () => {
      this.measure();
      this.setupResponsiveFeatures?.();
      this.requestRender(true);
    };

    this.prepare?.();
    this.setupResponsiveFeatures?.();
    this.measure();

    const staticScene = eclipseMotionPreference.matches || this.dataset.motion === 'false';
    if (staticScene || window.Shopify?.designMode) {
      this.renderEditorPreview?.();
      if (!this.renderEditorPreview) this.render?.(1);
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0]?.isIntersecting ?? true;
        if (this.visible) {
          this.measure();
          this.requestRender(true);
        }
      }, { rootMargin: '35% 0px' });
      this.observer.observe(this);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this);
    }

    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    eclipseMobileViewport.addEventListener?.('change', this.onResize);
    this.requestRender(true);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    eclipseMobileViewport.removeEventListener?.('change', this.onResize);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.cleanupResponsiveFeatures?.();
  }

  measure() {
    if (!this.isConnected) return;
    const rect = this.getBoundingClientRect();
    const scrollTop = eclipseScrollTop(this.scrollTarget);
    const viewportHeight = eclipseViewportHeight(this.scrollTarget);

    if (this.scrollTarget === window) {
      this.sceneStart = scrollTop + rect.top;
    } else {
      const containerRect = this.scrollTarget.getBoundingClientRect();
      this.sceneStart = scrollTop + rect.top - containerRect.top;
    }

    this.sceneRange = Math.max(1, this.offsetHeight - viewportHeight);
  }

  progress() {
    return eclipseClamp((eclipseScrollTop(this.scrollTarget) - this.sceneStart) / this.sceneRange);
  }

  requestRender(force = false) {
    if ((!this.visible && !force) || this.frame) return;
    this.frame = window.requestAnimationFrame((time) => {
      this.frame = 0;
      const interval = eclipseFrameInterval();
      if (!force && interval && time - this.lastRenderTime < interval) return;
      this.lastRenderTime = time;

      const progress = this.progress();
      if (!force && Math.abs(progress - this.lastProgress) < 0.0015) return;
      this.lastProgress = progress;
      this.render?.(progress);
    });
  }
}

class EclipseTotalityGate extends EclipseScrollScene {
  prepare() {
    this.counter = this.querySelector('[data-gate-count]');
    this.firstTransition = eclipseClamp((Number(this.dataset.phaseOneEnd) || 33) / 100, 0.1, 0.6);
    this.secondTransition = eclipseClamp((Number(this.dataset.phaseTwoEnd) || 68) / 100, this.firstTransition + 0.08, 0.92);
  }

  renderEditorPreview() {
    const previews = { signal: 0.12, totality: 0.5, entry: 0.88 };
    this.render(previews[this.dataset.editorPhase] ?? 0.88);
  }

  render(progress) {
    const value = Number(progress.toFixed(4));
    this.style.setProperty('--gate-progress', value);
    this.dataset.phase = value < this.firstTransition ? 'signal' : value < this.secondTransition ? 'totality' : 'entry';

    if (this.counter) {
      const count = Math.round(value * 100);
      if (count !== this.lastCount) {
        this.lastCount = count;
        this.counter.textContent = String(count).padStart(2, '0');
      }
    }
  }
}

class EclipseHorizontalCards {
  constructor(host, trackSelector, cardSelector, progressProperty, countSelector) {
    this.host = host;
    this.trackSelector = trackSelector;
    this.cardSelector = cardSelector;
    this.progressProperty = progressProperty;
    this.countSelector = countSelector;
    this.frame = 0;
    this.activeIndex = -1;
    this.onScroll = () => {
      if (!this.frame) this.frame = window.requestAnimationFrame(() => this.update());
    };
    this.onResize = () => {
      this.measure();
      this.update(true);
    };
  }

  connect() {
    this.disconnect();
    this.track = this.host.querySelector(this.trackSelector);
    if (!this.track || !eclipseMobileViewport.matches || this.host.dataset.mobileLayout === 'stack') return;

    this.cards = [...this.track.querySelectorAll(this.cardSelector)].filter((card) => !card.classList.contains('eclipse-hide-mobile'));
    if (!this.cards.length) return;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
      this.cards.forEach((card) => this.resizeObserver.observe(card));
    }

    this.measure();
    this.update(true);
  }

  disconnect() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.centers = [];
  }

  measure() {
    if (!this.track || !this.cards?.length) return;
    this.centers = this.cards.map((card) => card.offsetLeft + card.offsetWidth / 2);
    this.maxScroll = Math.max(1, this.track.scrollWidth - this.track.clientWidth);
  }

  nearestIndex(target) {
    if (!this.centers?.length) return 0;
    let low = 0;
    let high = this.centers.length - 1;

    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (this.centers[middle] < target) low = middle + 1;
      else high = middle;
    }

    if (low > 0 && Math.abs(this.centers[low - 1] - target) < Math.abs(this.centers[low] - target)) return low - 1;
    return low;
  }

  update(force = false) {
    this.frame = 0;
    if (!this.track || !this.cards?.length) return;
    if (!this.centers?.length) this.measure();

    const scrollLeft = Math.max(0, this.track.scrollLeft);
    const activeIndex = this.nearestIndex(scrollLeft + this.track.clientWidth / 2);
    const progress = eclipseClamp(scrollLeft / this.maxScroll);
    this.host.style.setProperty(this.progressProperty, progress.toFixed(4));

    if (force || activeIndex !== this.activeIndex) {
      this.activeIndex = activeIndex;
      this.cards.forEach((card, index) => {
        const active = index === activeIndex;
        card.classList.toggle('is-active', active);
        if (active) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });

      const count = this.host.querySelector(this.countSelector);
      if (count) count.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(this.cards.length).padStart(2, '0')}`;
    }
  }
}

class EclipseAccessOrbit extends EclipseScrollScene {
  prepare() {
    this.pieces = [...this.querySelectorAll('[data-orbit-piece]')];
    this.count = this.querySelector('[data-orbit-count]');
    const globalMotion = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--eclipse-global-motion')) || 1;
    this.motionIntensity = eclipseClamp(((Number(this.dataset.motionIntensity) || 100) / 100) * globalMotion, 0, 2.5);
  }

  setupResponsiveFeatures() {
    this.mobileCards ||= new EclipseHorizontalCards(
      this,
      '.eclipse-access-orbit__pieces',
      '.eclipse-access-orbit__piece',
      '--orbit-progress',
      '[data-orbit-count]'
    );
    this.mobileCards.connect();
  }

  cleanupResponsiveFeatures() {
    this.mobileCards?.disconnect();
  }

  render(progress) {
    if (eclipseMobileViewport.matches) return;
    const value = Number(progress.toFixed(4));
    this.style.setProperty('--orbit-progress', value);

    this.pieces.forEach((piece, index) => {
      const depth = (Number(piece.dataset.depth) || 5) * this.motionIntensity;
      const angle = (value * 1.35 + index / Math.max(1, this.pieces.length)) * Math.PI * 2;
      piece.style.setProperty('--orbit-x', `${Math.cos(angle) * depth * 4.8}px`);
      piece.style.setProperty('--orbit-y', `${Math.sin(angle) * depth * 3.7}px`);
      piece.style.setProperty('--orbit-r', `${Math.sin(angle) * depth * 0.42}deg`);
    });

    if (this.count && this.pieces.length) {
      const index = Math.min(this.pieces.length - 1, Math.floor(value * this.pieces.length));
      if (index !== this.lastCountIndex) {
        this.lastCountIndex = index;
        this.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.pieces.length).padStart(2, '0')}`;
      }
    }
  }
}

class EclipseCoreConstellation extends EclipseScrollScene {
  prepare() {
    this.pieces = [...this.querySelectorAll('[data-core-piece]')];
    const globalMotion = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--eclipse-global-motion')) || 1;
    this.motionIntensity = eclipseClamp(((Number(this.dataset.motionIntensity) || 100) / 100) * globalMotion, 0, 2.5);
  }

  setupResponsiveFeatures() {
    this.mobileCards ||= new EclipseHorizontalCards(
      this,
      '.eclipse-core-constellation__pieces',
      '.eclipse-core-constellation__piece',
      '--core-progress',
      '[data-core-count]'
    );
    this.mobileCards.connect();
  }

  cleanupResponsiveFeatures() {
    this.mobileCards?.disconnect();
  }

  render(progress) {
    if (eclipseMobileViewport.matches) return;
    const value = Number(progress.toFixed(4));
    this.style.setProperty('--core-progress', value);

    this.pieces.forEach((piece, index) => {
      const angle = (value + index * 0.31) * Math.PI * 2;
      const intensity = this.motionIntensity;
      piece.style.setProperty('--core-x-shift', `${Math.cos(angle) * (10 + index * 4) * intensity}px`);
      piece.style.setProperty('--core-y-shift', `${Math.sin(angle) * (8 + index * 3) * intensity}px`);
      piece.style.setProperty('--core-r-shift', `${Math.sin(angle) * 1.5 * intensity}deg`);
    });
  }
}

class EclipseUniverseGrid extends HTMLElement {
  connectedCallback() {
    this.mobileCards = new EclipseHorizontalCards(
      this,
      '.eclipse-universe-v3__grid',
      '.eclipse-universe-v3__card',
      '--universe-mobile-progress',
      '[data-universe-count]'
    );
    this.onResize = () => this.mobileCards.connect();
    window.addEventListener('resize', this.onResize, { passive: true });
    eclipseMobileViewport.addEventListener?.('change', this.onResize);
    this.mobileCards.connect();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    eclipseMobileViewport.removeEventListener?.('change', this.onResize);
    this.mobileCards?.disconnect();
  }
}

for (const [name, constructor] of [
  ['eclipse-totality-gate', EclipseTotalityGate],
  ['eclipse-access-orbit', EclipseAccessOrbit],
  ['eclipse-core-constellation', EclipseCoreConstellation],
  ['eclipse-universe-grid', EclipseUniverseGrid],
]) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

let eclipseRevealObserver;

function eclipseRevealImmediately(elements) {
  elements.forEach((element) => {
    element.classList.add('is-eclipse-visible');
    element.dataset.eclipseRevealReady = 'true';
  });
}

function eclipseInitialiseReveals(scope = document) {
  const elements = [...scope.querySelectorAll('[data-eclipse-reveal]')]
    .filter((element) => element.dataset.eclipseRevealReady !== 'true');
  if (!elements.length) return;

  const disabled = eclipseMotionPreference.matches
    || window.Shopify?.designMode
    || document.documentElement.dataset.eclipseSectionReveal !== 'true'
    || !('IntersectionObserver' in window);

  if (disabled) {
    eclipseRevealImmediately(elements);
    return;
  }

  eclipseRevealObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const mobileDelayScale = eclipseMobileViewport.matches ? 0.45 : 1;
      const delay = (Number(entry.target.dataset.eclipseDelay) || 0) * mobileDelayScale;
      entry.target.style.transitionDelay = `${Math.round(delay)}ms`;
      entry.target.classList.add('is-eclipse-visible');
      entry.target.dataset.eclipseRevealReady = 'true';
      eclipseRevealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.04, rootMargin: '0px 0px -4% 0px' });

  elements.forEach((element) => eclipseRevealObserver.observe(element));
}

function eclipseInitialiseScrollProgress() {
  const indicator = document.querySelector('[data-eclipse-scroll-progress]');
  if (!indicator) return;

  const target = eclipseScrollContainer();
  let frame = 0;
  const update = () => {
    frame = 0;
    const isWindow = target === window;
    const top = isWindow ? window.scrollY : target.scrollTop;
    const height = isWindow
      ? document.documentElement.scrollHeight - window.innerHeight
      : target.scrollHeight - target.clientHeight;
    indicator.style.transform = `scaleX(${height > 0 ? eclipseClamp(top / height) : 0})`;
  };
  const requestUpdate = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  target.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  update();
}

function eclipseInitialiseAnchorScrolling() {
  if (document.documentElement.dataset.eclipseSmoothAnchors !== 'true' || eclipseMotionPreference.matches) return;

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href^="#"]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href === '#') return;

    let target;
    try { target = document.querySelector(href); } catch (error) { return; }
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.pushState(null, '', href);
  });
}

function eclipseInitialise(scope = document) {
  document.documentElement.classList.add('eclipse-js');
  eclipseInitialiseReveals(scope);
}

eclipsePerformanceMode();
eclipseInitialise();
eclipseInitialiseScrollProgress();
eclipseInitialiseAnchorScrolling();
eclipseMobileViewport.addEventListener?.('change', eclipsePerformanceMode);

document.addEventListener('shopify:section:load', (event) => eclipseInitialise(event.target));
document.addEventListener('shopify:section:select', (event) => {
  eclipseInitialise(event.target);
  event.target.querySelectorAll('eclipse-totality-gate, eclipse-access-orbit, eclipse-core-constellation')
    .forEach((scene) => scene.requestRender?.(true));
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.documentElement.classList.remove('eclipse-preloader-active');
  document.querySelector('eclipse-preloader')?.remove();
});
